/**
 * Hierarchical field catalog for If/Else condition builder.
 * Categories mirror GHL-style grouping; fields map to workflow evaluation ids.
 */

const STANDARD_CONTACT_FIELDS = [
  { id: 'contact.last_appointment_at', label: 'Last appointment at', fieldType: 'date' },
  { id: 'contact.full_name', label: 'Full name', fieldType: 'text' },
  { id: 'contact.first_name', label: 'First name', fieldType: 'text' },
  { id: 'contact.last_name', label: 'Last name', fieldType: 'text' },
  { id: 'contact.email', label: 'Email', fieldType: 'text' },
  { id: 'contact.phone', label: 'Phone', fieldType: 'text' },
  { id: 'contact.company_name', label: 'Company name', fieldType: 'text' },
  { id: 'contact.full_address', label: 'Full address', fieldType: 'text' },
  { id: 'contact.address_1', label: 'Address 1', fieldType: 'text' },
  { id: 'contact.assigned_user', label: 'Assigned user', fieldType: 'text' },
  { id: 'contact.city', label: 'City', fieldType: 'text' },
  { id: 'contact.state', label: 'State', fieldType: 'text' },
  { id: 'contact.country', label: 'Country', fieldType: 'text' },
  { id: 'contact.timezone', label: 'Time zone', fieldType: 'text' },
  { id: 'contact.postal_code', label: 'Postal code', fieldType: 'text' },
  { id: 'contact.date_of_birth', label: 'Date of birth', fieldType: 'date' },
  { id: 'contact.source', label: 'Source', fieldType: 'text' },
  { id: 'contact.website', label: 'Website', fieldType: 'text' },
  { id: 'contact.tags', label: 'Tags', fieldType: 'tags' },
  { id: 'contact.contact_type', label: 'Contact type', fieldType: 'text' },
  { id: 'contact.dnd_enabled_channels', label: 'DND enabled channels', fieldType: 'text' },
  { id: 'contact.valid_whatsapp', label: 'Valid WhatsApp', fieldType: 'text' },
  { id: 'contact.valid_email', label: 'Valid email', fieldType: 'text' },
];

export const FALLBACK_CUSTOM_FIELDS = [
  { id: 'contact.custom.business_revenue', label: 'Busines Revenue', fieldType: 'text' },
  { id: 'contact.custom.business_age', label: 'Business Age', fieldType: 'text' },
  { id: 'contact.custom.contact_message', label: 'Contact Message', fieldType: 'text' },
  { id: 'contact.custom.credit_range', label: 'Credit Range', fieldType: 'text' },
  { id: 'contact.custom.do_you_have_a_business', label: 'Do You Have A Business?', fieldType: 'text' },
  { id: 'contact.custom.estimated_credit_score', label: 'Estimated Credit Score', fieldType: 'number' },
  { id: 'contact.custom.funding_amount_desired', label: 'Funding Amount Desired', fieldType: 'number' },
  { id: 'contact.custom.funding_reason', label: 'Funding Reason', fieldType: 'text' },
];

const FIRST_ATTRIBUTION_FIELDS = [
  { id: 'contact.attribution.first.campaign', label: 'Campaign (First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.campaign_id', label: 'Campaign ID(First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.medium_id', label: 'Medium ID(First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.utm_content', label: 'UTM Content (First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.utm_medium', label: 'UTM Medium (First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.medium', label: 'Medium (First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.utm_term', label: 'UTM Term (First Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.first.utm_source', label: 'UTM Source (First Attribution)', fieldType: 'text' },
];

const LAST_ATTRIBUTION_FIELDS = [
  { id: 'contact.attribution.last.campaign', label: 'Campaign (Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.campaign_id', label: 'Campaign ID(Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.medium_id', label: 'Medium ID(Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.utm_content', label: 'UTM Content (Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.utm_medium', label: 'UTM Medium (Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.medium', label: 'Medium (Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.term', label: 'Term (Last Attribution)', fieldType: 'text' },
  { id: 'contact.attribution.last.source', label: 'Source (Last Attribution)', fieldType: 'text' },
];

const COMPANY_FIELDS = [
  { id: 'company.name', label: 'Company Name', fieldType: 'text' },
  { id: 'company.phone', label: 'Phone', fieldType: 'text' },
  { id: 'company.email', label: 'Email', fieldType: 'text' },
  { id: 'company.website', label: 'Website', fieldType: 'text' },
  { id: 'company.address', label: 'Address', fieldType: 'text' },
  { id: 'company.state', label: 'State', fieldType: 'text' },
  { id: 'company.city', label: 'City', fieldType: 'text' },
  { id: 'company.description', label: 'Description', fieldType: 'text' },
  { id: 'company.postal_code', label: 'Postal Code', fieldType: 'text' },
  { id: 'company.country', label: 'Country', fieldType: 'text' },
];

function category(id, label, children = [], meta = {}) {
  return { id, label, type: 'category', children, ...meta };
}

function fieldNode(field) {
  return { ...field, type: 'field' };
}

/**
 * Build contact details subtree, optionally merging API custom fields.
 */
export function buildContactDetailsCategory(customFields = []) {
  const customFieldNodes = (customFields.length ? customFields : FALLBACK_CUSTOM_FIELDS).map(fieldNode);
  return category('contact_details', 'Contact details', [
    category('standard_fields', 'Standard fields', STANDARD_CONTACT_FIELDS.map(fieldNode)),
    category('custom_fields', 'Custom Fields', customFieldNodes),
    category('contact_first_attribution', 'Contact First Attribution', FIRST_ATTRIBUTION_FIELDS.map(fieldNode)),
    category('contact_last_attribution', 'Contact Last Attribution', LAST_ATTRIBUTION_FIELDS.map(fieldNode)),
  ]);
}

/**
 * Top-level field catalog. Some categories may be disabled via workflowContext.
 */
export function buildConditionFieldCatalog(customFields = []) {
  return [
    buildContactDetailsCategory(customFields),
    category('company', 'Company', COMPANY_FIELDS.map(fieldNode)),
    category('date_time', 'Date/Time', [], { disabledReason: null }),
    category('workflow_trigger', 'Workflow trigger', [], { disabledReason: null }),
    category('workflow_contact', 'Workflow contact', [], { disabledReason: null }),
    category('events', 'Events', [], { disabledReason: null }),
    category('custom_values', 'Custom values', [], { disabledReason: null }),
    category('contact_reply', 'Contact reply', [], {
      requires: 'contact_replied_wait',
      disabledReason: 'Add Email/SMS and Wait action (wait for - contact replied) before the If/Else action to use this recipe',
    }),
    category('inbound_email', 'Inbound email', [], { disabledReason: null }),
    category('appointment', 'Appointment', [], { disabledReason: null }),
    category('service_booking', 'Service booking', [], { disabledReason: null }),
    category('rental_booking', 'Rental booking', [], { disabledReason: null }),
    category('note', 'Note', [], { disabledReason: null }),
    category('ai_bot', 'AI Bot', [], { disabledReason: null }),
    category('opportunities', 'Opportunities', [], {
      requires: 'opportunity_context',
      disabledReason: "Please add an 'Opportunities' trigger or a 'Find Opportunity' action to use this recipe",
    }),
    category('ecommerce_stores', 'Ecommerce stores', [], { disabledReason: null }),
    category('invoice', 'Invoice', [], { disabledReason: null }),
    category('order', 'Order', [], {
      requires: 'order_submitted_trigger',
      disabledReason: 'Add Order Submitted trigger to use this recipe',
    }),
    category('payment', 'Payment', [], { disabledReason: null }),
    category('video_tracking', 'Video tracking', [], { disabledReason: null }),
    category('courses', 'Courses', [], { disabledReason: null }),
    category('inbound_webhook_trigger', 'Inbound webhook trigger', [], { disabledReason: null }),
    category('google_sheets_lookup', 'Google Sheets lookup', [], { disabledReason: null }),
    category('date_time_formatter', 'Date/time formatter', [], { disabledReason: null }),
    category('number_formatter', 'Number formatter', [], { disabledReason: null }),
    category('add_task', 'Add task', [], { disabledReason: null }),
    category('text_formatter', 'Text formatter', [], { disabledReason: null }),
    category('math_operation', 'Math operation', [], { disabledReason: null }),
    category('custom_webhook', 'Custom webhook', [], { disabledReason: null }),
    category('gpt', 'GPT', [], { disabledReason: null }),
    category('array_formatter', 'Array formatter', [], { disabledReason: null }),
    category('custom_code', 'Custom code', [], { disabledReason: null }),
    category('ai_agent', 'AI Agent', [], {
      requires: 'ai_agent_above',
      disabledReason: 'Please setup an AI Agent Action above this step to enable this condition',
    }),
    category('ivr_gather', 'IVR Gather', [], { disabledReason: null }),
    category('ivr_connect_call', 'IVR Connect call', [], { disabledReason: null }),
  ];
}

export const FALLBACK_TAG_OPTIONS = [
  'clients_whop',
  'confirmed_by_reply',
  'fmf_challenge_general_admission_may',
  'fmf_challenge_platinum_experience_may',
  'fmf_challenge_vip_gold',
  'imported credit repair clients',
  'payment link send',
  'priority_attendee',
];
